import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { parseISO } from 'date-fns';

import Button from 'components/Button';
import { Card } from 'components/Card';
import Message from 'components/Message';
import Spinner from 'components/Spinner';
import SvgDownload from 'components/Svgs/Download/Download';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';

import { intlMessage } from 'constants/propTypes';
import { ICONS } from 'constants/icons';

import { skuPayoutReportsMessages as messages } from './SkuPayoutReports.messages';

const SummaryRow = ({ label, value, currencyCode, isTotal }) => {
  const textClass = isTotal ? 'text--medium text--sm' : 'text--sm';

  return (
    <div className="flex content-space-between items-start sku-payout-report-detail__summary-row">
      <Message className={textClass} message={label} />
      <Message className={textClass} format="currency" value={value || 0} currency={currencyCode} />
    </div>
  );
};

SummaryRow.propTypes = {
  label: intlMessage.isRequired,
  value: PropTypes.number,
  currencyCode: PropTypes.string,
  isTotal: PropTypes.bool,
};

const formatPeriod = (intl, previousPayoutAsOfDate, payoutAsOfDate) => {
  const fmt = value => {
    if (!value) return null;
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : intl.formatDate(parsed, { month: 'short', day: 'numeric' });
  };
  const start = fmt(previousPayoutAsOfDate);
  const end = fmt(payoutAsOfDate);
  if (!start || !end) return null;
  return `${start} – ${end}`;
};

const SkuPayoutDetailSummary = ({ summary, venueName, city, onDownloadCsv, isExporting }) => {
  const intl = useIntl();
  if (!summary) return <Card loading />;

  const {
    currencyCode,
    totalVenuePayout,
    revenueFromReservations,
    payoutAsOfDate,
    previousPayoutAsOfDate,
    payoutSubmissionDate,
  } = summary;
  const postedDateValue = payoutSubmissionDate ? parseISO(payoutSubmissionDate) : null;
  const hasPostedDate = postedDateValue && !Number.isNaN(postedDateValue.getTime());
  const period = formatPeriod(intl, previousPayoutAsOfDate, payoutAsOfDate);

  return (
    <div className="sku-payout-report-detail__summary flex md-direction-row direction-column">
      <Card className="sku-payout-report-detail__summary-overview">
        <Message
          as="div"
          className="text--bold text--lg sku-payout-report-detail__summary-amount"
          format="currency"
          value={totalVenuePayout || 0}
          currency={currencyCode}
        />
        {period && <div className="text--medium text--sm">{period}</div>}
        {venueName && <div className="text--sm">{[venueName, city].filter(Boolean).join(', ')}</div>}
        {hasPostedDate && (
          <div className="text--sm text--light sku-payout-report-detail__posted-on">
            <Message
              message={messages.summaryPostedOn}
              values={{ date: <Message format="short-w-2-digit-year" value={postedDateValue} /> }}
            />
            <TooltipToggle name="sku-payout-report-detail-posted-on" className="sku-payout-report-detail__info-icon">
              <Isvg src={ICONS.infoGrey} />
            </TooltipToggle>
            <TooltipContent name="sku-payout-report-detail-posted-on" place="top">
              <Message message={messages.postedOnTooltip} />
            </TooltipContent>
          </div>
        )}
      </Card>

      <Card className="sku-payout-report-detail__summary-breakdown">
        <div className="sku-payout-report-detail__summary-revenue">
          <SummaryRow
            label={messages.summaryTotalEarnings}
            value={revenueFromReservations}
            currencyCode={currencyCode}
          />
        </div>
        <div className="sku-payout-report-detail__summary-divider" aria-hidden="true" />
        <SummaryRow label={messages.summaryTotalPayout} value={totalVenuePayout} currencyCode={currencyCode} isTotal />
      </Card>

      <div className="sku-payout-report-detail__summary-actions">
        <Button styled="primary" disabled={isExporting} onClick={onDownloadCsv}>
          {isExporting ? (
            <>
              <Spinner />
              <Message message={messages.downloadCsvGenerating} />
            </>
          ) : (
            <>
              <SvgDownload className="sku-payout-report-detail__download-icon" />
              <Message message={messages.downloadCsv} />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

SkuPayoutDetailSummary.propTypes = {
  summary: PropTypes.shape({
    currencyCode: PropTypes.string,
    totalVenuePayout: PropTypes.number,
    revenueFromReservations: PropTypes.number,
    payoutAsOfDate: PropTypes.string,
    previousPayoutAsOfDate: PropTypes.string,
    payoutSubmissionDate: PropTypes.string,
  }),
  venueName: PropTypes.string,
  city: PropTypes.string,
  onDownloadCsv: PropTypes.func.isRequired,
  isExporting: PropTypes.bool,
};

SkuPayoutDetailSummary.defaultProps = {
  isExporting: false,
};

export default SkuPayoutDetailSummary;
